import { useEffect, useState } from "react";
import { Button, Col, Row, Select, Space, DatePicker } from "antd";
import usePayouts from "hooks/finance/usePayouts";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useUsers from "hooks/userManagement/useUsers";
import PayoutChargesTable from "./PayoutChargesTable"; // New table for charges
import dayjs from "dayjs";
const { RangePicker } = DatePicker;

export default function PayoutChargesPage() {
  const {
    charges,
    chargesMeta,
    fetchCharges,
    fetchUserChargesInRange,
    loading,
  } = usePayouts();
  const [pagination, setPagination] = useState({ page: 0, size: 10 });

  const { searchUserProfiles } = useUsers(0, 20, null, true, { autoFetch: false });

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: dayjs().startOf("month").toISOString(),
    end: dayjs().endOf("month").toISOString(),
  });
  const [activePreset, setActivePreset] = useState("month");

  // fetch charges whenever user or date range changes
  useEffect(() => {
    const fetchParams = {
      page: pagination.page,
      size: pagination.size,
      start: dateRange.start,
      end: dateRange.end,
      userId: selectedUserId,
    };

    if (!selectedUserId) {
      fetchCharges(fetchParams);
    } else {
      fetchUserChargesInRange(fetchParams);
    }
  }, [
    selectedUserId,
    dateRange,
    pagination.page,
    pagination.size,
    fetchCharges,
    fetchUserChargesInRange,
  ]);

  const handleSearchUser = async (value) => {
    if (!value) return setUserOptions([]);

    try {
      const users = await searchUserProfiles(value);

      setUserOptions(
        users.map((u) => ({
          label: `${u.firstName} ${u.lastName} (${u.email})`,
          value: u.id,
        })),
      );
    } catch (err) {
      console.error("Failed to search users", err);
    }
  };

  //   console.log(charges);
  //   console.log(chargesMeta);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="money-bill-wave"
          title="Payout Charges"
          subtitle="View all payout charges processed in the system"
          className="mb-3 mt-3 flex-1"
        />
      </div>

      {/* filter bar */}
      <Row gutter={[10, 10]} style={{ marginBottom: 16, alignItems: "center" }}>
        <Col>
          <Select
            showSearch={{
              filterOption: false,
              onSearch: handleSearchUser,
            }}
            placeholder="Search and select user"
            allowClear
            onChange={(val) => setSelectedUserId(val)}
            options={userOptions}
            style={{ width: 300 }}
          />
        </Col>

        <Col style={{ marginLeft: "auto" }}>
          <Space>
            <Button
              type={activePreset === "month" ? "primary" : "default"}
              onClick={() => {
                setDateRange({
                  start: dayjs().startOf("month").toISOString(),
                  end: dayjs().endOf("month").toISOString(),
                });
                setActivePreset("month");
                setPagination({ page: 0, size: pagination.size });
              }}
            >
              This Month
            </Button>
            <Button
              type={activePreset === "year" ? "primary" : "default"}
              onClick={() => {
                setDateRange({
                  start: dayjs().startOf("year").toISOString(),
                  end: dayjs().endOf("year").toISOString(),
                });
                setActivePreset("year");
                setPagination({ page: 0, size: pagination.size });
              }}
            >
              This Year
            </Button>

            <RangePicker
              value={
                dateRange.start && dateRange.end
                  ? [dayjs(dateRange.start), dayjs(dateRange.end)]
                  : []
              }
              onChange={(dates) => {
                if (!dates || dates.length === 0) {
                  setDateRange({ start: null, end: null });
                } else {
                  setDateRange({
                    start: dates[0].startOf("day").toISOString(),
                    end: dates[1].endOf("day").toISOString(),
                  });
                }
                setPagination({ page: 0, size: pagination.size });
              }}
            />

            <Button
              onClick={() => {
                setDateRange({ start: null, end: null });
                setActivePreset(null);
                setPagination({ page: 0, size: pagination.size });
              }}
            >
              Clear Date Filter
            </Button>
          </Space>
        </Col>
      </Row>

      <PayoutChargesTable
        charges={charges}
        loading={loading}
        meta={chargesMeta}
        onFetchCharges={({ page, size }) =>
          setPagination({ page: page - 1, size })
        }
      />
    </div>
  );
}
